import { useEffect, useState, type ReactElement } from "react";

import { api, errMessage } from "../../api/client";
import { IRB_STUDY_LABELS } from "../../api/labels";
import type { AdminSubstudy, Batch, IrbStudy } from "../../api/types";
import { SkeletonRows } from "../Skeleton";
import { BatchTable, BatchUploadForm, useBatchList } from "../shared/BatchTable";
import { downloadCsv } from "./download";

// Photos per REQUEST — not per batch; bigger selections are streamed as
// sequential chunks. Must stay under BOTH the API's per-request cap
// (admin/router.py ADMIN_MAX_BATCH_PHOTOS = 999) and Starlette's multipart
// parser limit of 1000 files per request, which counts the manifest riding along.
const CHUNK_PHOTOS = 500;

// Bytes per REQUEST. A count cap alone doesn't bound a request: 500 photos at
// the 10 MiB ceiling is ~5 GB, which nginx 413s (the batch endpoints cap at 1g
// — nginx/faceage.conf) and the chunk is lost. Stay well under that so
// multipart overhead can't push a chunk over the line.
const CHUNK_BYTES = 256 * 1024 * 1024;

// Split a selection into requests that respect BOTH caps. A file larger than
// CHUNK_BYTES still gets its own chunk rather than looping forever — the API
// rejects it on its own terms (MAX_PHOTO_BYTES), which is the error we want.
export function chunkFiles(files: File[]): File[][] {
  const chunks: File[][] = [];
  let current: File[] = [];
  let bytes = 0;
  for (const file of files) {
    if (current.length > 0 && (current.length >= CHUNK_PHOTOS || bytes + file.size > CHUNK_BYTES)) {
      chunks.push(current);
      current = [];
      bytes = 0;
    }
    current.push(file);
    bytes += file.size;
  }
  if (current.length > 0) chunks.push(current);
  return chunks;
}

// Admin bulk upload: re-run stored photos through the current inference
// pipeline under any substudy — e.g. reprocessing InsightFace-era submissions on
// the reference detector. Same manifest+photos contract as the collab Batches
// tab (form/table/poll shared via components/shared), but substudy-selectable
// and served by the /admin batch endpoints.
export function BatchUploadCard({ substudies }: { substudies: AdminSubstudy[] }): ReactElement {
  // Protocol first, then the substudy within it. BOTH are chosen explicitly and
  // neither is pre-filled: IRB attribution is not a thing to have a default for.
  // This card used to POST /admin/substudies/default on mount and pre-select the
  // house study — which is healthy_volunteers — so a re-run of MGB-patient
  // photos landed under the wrong protocol unless the admin noticed a dropdown.
  // Naming the protocol is redundant with the substudy (substudies.irb_study
  // decides), and that is the point: it makes the admin state the answer, and
  // the substudy list can only offer studies that agree with it.
  const [irbStudy, setIrbStudy] = useState<IrbStudy | "">("");
  const [substudyId, setSubstudyId] = useState("");
  // Separate from the list's `error`, which renders in the table's place — a
  // failed download must not blank out the table it was launched from.
  const [downloadError, setDownloadError] = useState<string | null>(null);

  const substudiesForProtocol = irbStudy
    ? substudies.filter((t) => t.irb_study === irbStudy)
    : [];

  const [busy, setBusy] = useState(false);
  const [progress, setProgress] = useState<string | null>(null);

  // The target as the API takes it: protocol always, substudy only when one is
  // chosen. Omitting substudy_id means the no-substudy batches under that
  // protocol — a target, not a missing filter.
  const targetQuery = irbStudy
    ? `irb_study=${irbStudy}${substudyId ? `&substudy_id=${substudyId}` : ""}`
    : null;

  const { batches, loading, error, reload } = useBatchList(
    targetQuery ? `/admin/batches?${targetQuery}` : null,
  );

  useEffect(() => {
    // A new target invalidates a download error from the previous one — the
    // shared hook's poll reuses reload() directly, so a mid-poll error still shows.
    setDownloadError(null);
  }, [targetQuery]);

  // Every create carries the declared target. substudy_id is simply absent when
  // none was chosen — that IS the "no substudy" case, not a missing field.
  function appendTarget(form: FormData): void {
    form.append("irb_study", irbStudy);
    if (substudyId) form.append("substudy_id", substudyId);
  }

  async function upload(name: string, manifest: File, files: File[]): Promise<void> {
    if (!irbStudy) return;
    setBusy(true);
    const chunks = chunkFiles(files);
    try {
      if (chunks.length === 1) {
        const form = new FormData();
        form.append("name", name);
        appendTarget(form);
        form.append("manifest", manifest);
        for (const file of files) form.append("photos", file);
        await api<Batch>("POST", "/admin/batches", { form });
      } else {
        // One click, one batch: start → photo chunks (the manifest rides along
        // with each chunk for validation) → complete.
        const startForm = new FormData();
        startForm.append("name", name);
        appendTarget(startForm);
        startForm.append("manifest", manifest);
        const started = await api<Batch>("POST", "/admin/batches/start", {
          form: startForm,
        });
        setProgress(`0 / ${files.length.toLocaleString()}`);
        for (const chunk of chunks) {
          const chunkForm = new FormData();
          chunkForm.append("manifest", manifest);
          for (const file of chunk) chunkForm.append("photos", file);
          const updated = await api<Batch>("POST", `/admin/batches/${started.id}/photos`, {
            form: chunkForm,
          });
          // Server truth, not the loop counter: how many photos the batch now
          // actually holds. The list below refreshes alongside, so a mid-upload
          // batch is visible growing.
          const received =
            updated.processed_photos + updated.failed_photos + updated.pending_photos;
          setProgress(`${received.toLocaleString()} / ${files.length.toLocaleString()}`);
          await reload();
        }
        await api<Batch>("POST", `/admin/batches/${started.id}/complete`);
      }
      await reload();
    } catch (err) {
      await reload(); // a mid-upload failure still shows the partial batch state
      throw err; // the shared form shows the ApiError message
    } finally {
      setBusy(false);
      setProgress(null);
    }
  }

  // Reported rather than swallowed: this endpoint is under ADMIN_EXPORT_LIMIT
  // (6/min), so a busy admin hits a 429 in normal use — and an uncaught
  // rejection here just looks like a button that does nothing.
  async function saveBatchCsv(batch: Batch): Promise<void> {
    setDownloadError(null);
    try {
      await downloadCsv(`/admin/batches/${batch.id}/results.csv`, `batch-${batch.id}-results.csv`);
    } catch (err) {
      setDownloadError(errMessage(err, "Could not download the results CSV."));
    }
  }

  return (
    <section className="app-card">
      <h2>Batch Upload (Pipeline Re-run)</h2>
      <p className="muted">
        Re-run photos through the current inference pipeline under a substudy. Prepare a manifest
        CSV with header <code>filename,email</code>: one row per photo, linking each
        photo&apos;s exact filename to the subject&apos;s email address. Photos: JPEG, PNG, or
        HEIC, up to 10&nbsp;MB each, any number per batch (large selections upload in chunks
        automatically); every photo must appear in the manifest. Per-photo estimates come back
        in the results CSV.
      </p>
      <div className="field-grid">
        <label className="field">
          <span>IRB protocol to upload under</span>
          <select
            value={irbStudy}
            onChange={(e) => {
              setIrbStudy(e.target.value as IrbStudy | "");
              setSubstudyId(""); // the chosen substudy may not belong to the new protocol
            }}
          >
            <option value="">Select…</option>
            {(Object.entries(IRB_STUDY_LABELS) as [IrbStudy, string][]).map(([value, label]) => (
              <option key={value} value={value}>
                {label}
              </option>
            ))}
          </select>
        </label>
        <label className="field">
          <span>Substudy to upload under</span>
          <select
            value={substudyId}
            disabled={!irbStudy}
            onChange={(e) => setSubstudyId(e.target.value)}
          >
            <option value="">
              {irbStudy ? "No substudy (the protocol directly)" : "Pick a protocol first"}
            </option>
            {substudiesForProtocol.map((t) => (
              <option key={t.id} value={t.id}>
                {t.name} ({t.slug})
              </option>
            ))}
          </select>
        </label>
      </div>
      {irbStudy && !substudyId ? (
        <p className="muted">
          Uploading for participants with no substudy. They must already exist and must fall under
          the protocol above. This path adds photos to people; it never creates them.
        </p>
      ) : null}
      {irbStudy ? (
        <>
          <BatchUploadForm
            busy={busy}
            busyLabel={progress ? `Uploading… ${progress}` : "Uploading…"}
            namePlaceholder="e.g. InsightFace-era re-run"
            onUpload={upload}
          />
          {loading ? (
            <SkeletonRows rows={2} />
          ) : error ? (
            <p className="form-error">{error}</p>
          ) : batches.length === 0 ? (
            <p className="muted">No batches for this substudy yet.</p>
          ) : (
            <BatchTable batches={batches} onDownload={(b) => void saveBatchCsv(b)} />
          )}
          {downloadError ? <p className="form-error">{downloadError}</p> : null}
        </>
      ) : null}
    </section>
  );
}
